import React from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, UserX, ArrowRight } from 'lucide-react';
interface SlideProps {
  index: number;
  total: number;
}
export function Slide2UKChallenge({
  index
}: SlideProps) {
  // The real problem: a registration → attendance conversion gap on LOCAL webinars,
  // plus the HCPs who never register at all. Evergreen on-demand content captures both.
  const funnel = [{
    value: '100%',
    label: 'Register for local webinars',
    note: 'Registrations are "quite popular"',
    h: 'h-full',
    tone: 'bg-[var(--brand-green-lighter)]',
    text: 'text-[var(--brand-green)]'
  }, {
    value: '~30%',
    label: 'Actually attend live',
    note: 'A low attendance rate',
    h: 'h-[30%]',
    tone: 'bg-[var(--brand-green)]',
    text: 'text-white'
  }];
  return <div className="relative w-screen h-screen overflow-hidden bg-[var(--bg)] p-[clamp(2rem,4vw,4rem)] flex flex-col" data-id="element-56">
      {/* Header */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2,
      duration: 0.6
    }} className="mb-[clamp(1.5rem,2.5vw,2.5rem)] flex-shrink-0" data-id="element-57">
        <p className="text-[var(--brand-green)] text-[clamp(0.875rem,1.5vw,1.125rem)] font-semibold uppercase tracking-wider mb-[clamp(0.5rem,1vw,1rem)]" data-id="element-58">
          The Challenge
        </p>
        <h2 className="text-[var(--fg)] font-bold text-[clamp(2rem,3.6vw,3.25rem)] leading-[1.08] max-w-5xl" data-id="element-59">
          UK HCPs register for webinars — but{' '}
          <span className="text-[var(--brand-green-light)]" data-id="element-60">
            most never actually attend.
          </span>
        </h2>
      </motion.div>

      {/* Conversion gap */}
      <div className="flex-1 grid grid-cols-12 gap-[clamp(1.5rem,3vw,3rem)] min-h-0" data-id="element-61">
        {/* Registration → attendance conversion */}
        <div className="col-span-8 flex flex-col min-h-0" data-id="element-62">
          <div className="flex items-center gap-2 mb-3 flex-shrink-0" data-id="element-63">
            <TrendingDown className="w-5 h-5 text-[var(--muted)]" data-id="element-64" />
            <span className="text-[var(--muted)] text-[clamp(0.8rem,1vw,1rem)] font-semibold uppercase tracking-wide" data-id="element-65">
              The local webinar conversion gap
            </span>
          </div>

          <div className="flex-1 grid grid-cols-2 gap-[clamp(1rem,2vw,2.5rem)] items-end min-h-0" data-id="element-66">
            {funnel.map((r, i) => <div key={i} className="h-full flex flex-col justify-end min-h-0" data-id="element-67">
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.4 + i * 0.25,
              duration: 0.4
            }} className="mb-2 flex-shrink-0" data-id="element-68">
                  <p className="font-bold text-[clamp(2rem,4vw,3.5rem)] leading-none text-[var(--brand-green)]" data-id="element-69">
                    {r.value}
                  </p>
                </motion.div>
                <div className="flex-1 flex flex-col justify-end min-h-0 relative" data-id="element-70">
                  {/* ghost of full registration height behind the attended bar */}
                  {i === 1 && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 1,
                duration: 0.5
              }} className="absolute inset-x-0 bottom-0 top-0 rounded-t-xl border-2 border-dashed border-[var(--muted)]/40 flex items-start justify-center pt-3" data-id="element-71">
                      <span className="text-[var(--muted)] text-[clamp(0.7rem,0.85vw,0.85rem)] font-bold flex items-center gap-1.5" data-id="element-72">
                        <UserX className="w-4 h-4" data-id="element-73" /> ~70% no-show
                      </span>
                    </motion.div>}
                  <motion.div initial={{
                scaleY: 0
              }} animate={{
                scaleY: 1
              }} transition={{
                delay: 0.5 + i * 0.25,
                duration: 0.6,
                ease: [0.4, 0, 0.2, 1]
              }} style={{
                transformOrigin: 'bottom'
              }} className={`${r.h} ${r.tone} rounded-t-xl flex items-start justify-center pt-3 px-2 relative z-10`} data-id="element-74">
                    <span className={`text-[clamp(0.75rem,0.95vw,0.95rem)] font-bold text-center ${r.text}`} data-id="element-75">
                      {r.label}
                    </span>
                  </motion.div>
                </div>
                <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.8 + i * 0.25,
              duration: 0.4
            }} className="text-[var(--muted)] text-[clamp(0.7rem,0.85vw,0.85rem)] text-center mt-2 leading-snug flex-shrink-0" data-id="element-76">
                  {r.note}
                </motion.p>
              </div>)}
          </div>
        </div>

        {/* The two missed audiences */}
        <motion.div initial={{
        opacity: 0,
        x: 24
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.6,
        duration: 0.6
      }} className="col-span-4 bg-[var(--surface)] rounded-2xl p-[clamp(1.5rem,2vw,2rem)] flex flex-col justify-center border border-black/5" data-id="element-77">
          <p className="text-[var(--fg)] text-[clamp(1.05rem,1.5vw,1.5rem)] font-semibold leading-snug mb-4" data-id="element-78">
            Live webinars miss most UK HCPs.
          </p>
          <p className="text-[var(--muted)] text-[clamp(0.85rem,1.1vw,1.15rem)] leading-relaxed" data-id="element-79">
            The science never reaches two large groups: those who{' '}
            <span className="font-semibold text-[var(--fg)]" data-id="element-80">
              register but don't attend
            </span>
            , and those who{' '}
            <span className="font-semibold text-[var(--fg)]" data-id="element-81">
              never register for global or local webinars at all
            </span>
            .
          </p>
        </motion.div>
      </div>

      {/* Opportunity pivot */}
      <motion.div initial={{
      opacity: 0,
      y: 24
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.2,
      duration: 0.6
    }} className="mt-[clamp(1.5rem,2.5vw,2.5rem)] bg-[var(--brand-green)] rounded-2xl p-[clamp(1.5rem,2.5vw,2.5rem)] flex items-center justify-between gap-6 flex-shrink-0 shadow-lg" data-id="element-82">
        <div data-id="element-83">
          <p className="text-[var(--brand-green-lighter)] text-[clamp(0.8rem,1vw,1rem)] font-semibold uppercase tracking-wider mb-1" data-id="element-84">
            The opportunity
          </p>
          <p className="text-white font-bold text-[clamp(1.25rem,2.2vw,2rem)] leading-tight" data-id="element-85">
            Evergreen global content, localized for the UK —
          </p>
          <p className="text-white/80 text-[clamp(0.95rem,1.25vw,1.25rem)] mt-1" data-id="element-86">
            on-demand episodes engage the{' '}
            <span className="font-bold text-white" data-id="element-87">
              no-shows and never-registered
            </span>{' '}
            without requiring live attendance.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-88">
          <div className="text-right" data-id="element-89">
            <p className="text-[var(--brand-green-lighter)] text-sm font-medium" data-id="element-90">
              ~30% live
            </p>
            <ArrowRight className="w-6 h-6 text-white/50 inline" data-id="element-91" />
          </div>
          <div className="bg-white rounded-2xl px-6 py-4 text-center" data-id="element-92">
            <p className="text-[var(--brand-green)] font-bold text-[clamp(1.75rem,3vw,2.75rem)] leading-none" data-id="element-93">
              Anytime
            </p>
            <p className="text-[var(--muted)] text-xs font-semibold mt-1" data-id="element-94">
              on-demand reach
            </p>
          </div>
        </div>
      </motion.div>
    </div>;
}